import classNames from 'classnames';
import React, { useContext, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { keyframes } from 'glamor';
import { useHistory } from 'react-router-dom';

import { calculatePaymentFee } from '../../utils/useCartDetails';
import { getPaymentDisplayName, getPaymentInfo } from '../../utils';

import { useRootSelector } from 'rootStore';
import { createStyle, createStyles } from 'utils/createStyle';
import { monetizeWithDecimal } from 'common/utils/strings';
import { OrderScreenContext } from 'features/order-summary/screens/CheckoutScreen';
import { EASE_IN_OUT_BACK } from 'styles/easing';
import { getPath, Routes } from 'routes';
import { LinkBtn } from 'components/link-btn';

export const hideHeight = keyframes({
    '0%': {
        maxHeight: '200px',
    },
    '100%': {
        maxHeight: '0px',
    },
});

export const showHeight = keyframes({
    '0%': {
        maxHeight: '0px',
    },
    '99%': {
        maxHeight: '200px',
    },
    '100%': {
        maxHeight: 'auto',
    },
});

const styles = createStyles({
    container: createStyle(''),
    title: ['text-brand', { fontSize: '17px' }],
    description: 'text-sm text-grey-1 ml-4',
    subtotal: 'text-grey-1 font-bold flex flex-row align-center',
    subtotalItems: createStyle('grid', {
        gridTemplateColumns: 'max-content auto',
    }),
    area: [
        'mb-2',
        {
            overflow: 'hidden',
            animationName: showHeight,
            animationDuration: '.5s',
            animationTimingFunction: EASE_IN_OUT_BACK,
            animationFillMode: 'forwards',
        },
    ],
    hidden: {
        overflow: 'hidden',
        animationName: hideHeight,
        animationDuration: '.5s',
        animationTimingFunction: EASE_IN_OUT_BACK,
        animationFillMode: 'forwards',
    },
});

export interface OrderSummaryOptionsProps {
    shipToHome: boolean;
    hidePayment?: boolean;
}

export const OrderSummaryOptions = ({
    shipToHome,
    hidePayment,
    children,
}: React.PropsWithChildren<OrderSummaryOptionsProps>) => {
    return (
        <div className={styles.container}>
            {!hidePayment && <PaymentSection shipToHome={shipToHome} />}
            <ShipToSection shipToHome={shipToHome} />
            <ShippingMethodSection shipToHome={shipToHome} />
            <TotalSection shipToHome={shipToHome} />
            <EarningsSection />
            {children}
        </div>
    );
};

export const EarningsSection = () => {
    const cart = useRootSelector((s) => s.store.cart.cart);
    return (
        <div className={classNames(styles.area, 'mt-2')}>
            <div
                className={classNames(
                    styles.title,
                    'flex flex-row text-success bg-grey-7 px-2 py-1 text-lg'
                )}
            >
                <div>Earnings</div>
                <div className="ml-auto font-bold">
                    {cart && monetizeWithDecimal(cart.earnings)}
                </div>
            </div>
        </div>
    );
};

export const ShippingMethodSection = ({ shipToHome }: { shipToHome: boolean }) => {
    const history = useHistory();
    const { cartInfo, cartInfoDetails } = useContext(OrderScreenContext);
    const {
        shipToHomeItems,
        hasShipToHomeEligibleItems,
        shipping: { selectedShipping, isLoading: isLoadingShipping },
    } = cartInfoDetails;

    const shipToHomeIfPossible = shipToHome && hasShipToHomeEligibleItems;
    const hasPaymentMethod = cartInfo.paymentMethodId;

    const goToShipping = (e: React.MouseEvent) => {
        history.push(Routes.ShopOrderShipping, cartInfo);
        e.preventDefault();
    };
    return (
        <div className={`${shipToHomeItems.length <= 0 ? styles.hidden : styles.area}`}>
            <div className={classNames(styles.title, 'flex items-center')}>
                <span>Shipping Method</span>
                {shipToHomeIfPossible && hasPaymentMethod && (
                    <LinkBtn className="ml-auto text-xs" onClick={goToShipping}>
                        Edit
                    </LinkBtn>
                )}
            </div>
            <div className={styles.description}>
                {isLoadingShipping && <Skeleton />}
                {!isLoadingShipping && <div>{selectedShipping ? selectedShipping.name : ''}</div>}
            </div>
        </div>
    );
};

export const ShipToSection = ({ shipToHome }: { shipToHome: boolean }) => {
    const history = useHistory();
    const { cartInfo, cartInfoDetails } = useContext(OrderScreenContext);
    const {
        shipToHomeItems,
        hasShipToHomeEligibleItems,
        shipToCoordinator,
        shipping: { selectedAddress },
    } = cartInfoDetails;

    const shipToHomeIfPossible = shipToHome && hasShipToHomeEligibleItems;
    const hasPaymentMethod = cartInfo.paymentMethodId;

    const goToShipping = (e: React.MouseEvent) => {
        history.push(Routes.ShopOrderShipping, cartInfo);
        e.preventDefault();
    };

    const addressPart1 = useMemo(() => {
        if (!selectedAddress) {
            return '';
        }

        return selectedAddress.address2
            ? `${selectedAddress.address1} ${selectedAddress.address2}`
            : selectedAddress.address1;
    }, [selectedAddress]);

    const addressPart2 = useMemo(() => {
        if (!selectedAddress) {
            return '';
        }

        return `${selectedAddress.city} ${selectedAddress.state} ${selectedAddress.zipCode}`;
    }, [selectedAddress]);

    let totalShipToCoordinatorItems = 0;
    let totallShipToHomeItems = 0;

    for (let i = 0; i < shipToHomeItems.length; ++i) {
        totallShipToHomeItems += shipToHomeItems[i].quantity || 1;
    }

    for (let i = 0; i < shipToCoordinator.length; ++i) {
        totalShipToCoordinatorItems += shipToCoordinator[i].quantity || 1;
    }

    return (
        <div className={styles.area}>
            <div className={classNames(styles.title, 'flex justify-between items-center')}>
                <span>Ship to</span>
                {shipToHomeIfPossible && hasPaymentMethod && (
                    <LinkBtn className="ml-auto text-xs" onClick={goToShipping}>
                        Edit
                    </LinkBtn>
                )}
            </div>
            <ul className="list-disc ml-4">
                {totalShipToCoordinatorItems > 0 && (
                    <li className={styles.description}>
                        Ship to coordinator: {totalShipToCoordinatorItems} card
                        {totalShipToCoordinatorItems > 1 ? 's' : ''}
                    </li>
                )}
                {totallShipToHomeItems > 0 && (
                    <li className={styles.description}>
                        Ship to home: {totallShipToHomeItems} card
                        {totallShipToHomeItems > 1 ? 's' : ''}
                        {selectedAddress && (
                            <div className="ml-2">
                                <div>{addressPart1}</div>
                                <div>{addressPart2}</div>
                            </div>
                        )}
                    </li>
                )}
            </ul>
        </div>
    );
};

export const TotalSection = ({ shipToHome }: { shipToHome: boolean }) => {
    const { cartInfoDetails } = useContext(OrderScreenContext);
    const {
        hasShipToHomeEligibleItems,
        shipping: { selectedShipping, isLoading: isLoadingShipping, shippingFee },
        payment: { paymentFee },
        total,
    } = cartInfoDetails;
    const cart = useRootSelector((s) => s.store.cart.cart);
    const shipToHomeIfPossible = shipToHome && hasShipToHomeEligibleItems;

    return (
        <>
            <div className={styles.area}>
                <div className={styles.subtotal}>
                    <div>Subtotal</div>
                    <div className="ml-auto">{cart && monetizeWithDecimal(cart.subtotal)}</div>
                </div>
                <div className={classNames(styles.description, styles.subtotalItems)}>
                    {paymentFee && paymentFee.amount > 0 && (
                        <>
                            <div>{getPaymentDisplayName(paymentFee.paymentType)}</div>
                            <div className="text-right">
                                {monetizeWithDecimal(
                                    calculatePaymentFee(
                                        (cart?.subtotal || 0) + shippingFee,
                                        paymentFee
                                    )
                                )}
                            </div>
                        </>
                    )}
                    {shipToHomeIfPossible && (
                        <>
                            {!isLoadingShipping && (
                                <>
                                    <div>Shipping &amp; Handling</div>
                                    <div className="text-right">
                                        {selectedShipping
                                            ? monetizeWithDecimal(selectedShipping.cost)
                                            : '-'}
                                    </div>
                                </>
                            )}
                            {isLoadingShipping && (
                                <div className="col-span-2">
                                    <Skeleton />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <div className={classNames(styles.area, 'mb-3')}>
                <div className={classNames(styles.title, 'flex flex-row')}>
                    <div>Total</div>
                    <div className="ml-auto text-grey-1 font-bold">
                        {monetizeWithDecimal(total)}
                    </div>
                </div>
            </div>
        </>
    );
};

export const PaymentSection = ({ shipToHome }: { shipToHome: boolean }) => {
    const { cartInfo, cartInfoDetails } = useContext(OrderScreenContext);
    const {
        payment: { paymentAccount, isLoading: isLoadingPayment },
    } = cartInfoDetails;
    const history = useHistory();

    const goToPayment = (e: React.MouseEvent) => {
        const url = getPath(Routes.ShopOrderPayment, {
            shipToHome: shipToHome ? 'ship-to-home' : 'ship-to-coordinator',
        });
        history.push(url, cartInfo);
        e.preventDefault();
    };

    return (
        <div className={styles.area}>
            <div className={classNames(styles.title, 'flex justify-between items-center')}>
                <span>Payment</span>
                <LinkBtn className="ml-auto text-xs" onClick={goToPayment}>
                    Edit
                </LinkBtn>
            </div>
            {!isLoadingPayment && (
                <div className={styles.description}>
                    <div data-recording-sensitive>{getPaymentInfo(paymentAccount)}</div>
                </div>
            )}
            {isLoadingPayment && <Skeleton className={styles.description} />}
        </div>
    );
};
